import { lazy } from 'react'

import { useQuery, makeVar } from '@apollo/client'

import { CLICK_CODE } from '@local/utils'

import ErrorPage from '@web/components/ErrorPage'
import ViewCardImageLoader from '@web/components/Loader/ViewImageCardLoader/ViewImageCardLoader'
import { gql } from '@web/federation/gql'
import useAppBgColor, { AppBgColors } from '@web/hooks/useAppBgColor'
import { initializeFederationApollo } from '@web/libs/apollo/federationClient'
import { extractByTypename } from '@web/libs/apollo/typeGuard'
import { extractErrorMessage, neloLogger } from '@web/libs/utils'
import LazyIntersectionLoader from '@web/pages/home/components/LazyIntersectionLoader'
import { UPCOMING_PAGE_SIZE } from '@web/pages/timeline/components/TimelineUpcoming'
import CheckInButtonContainer from '@web/pages/v5/home/CheckInButton'
import FooterSection from '@web/pages/v5/home/FooterSection'
import HomeHeaderContainer from '@web/pages/v5/home/HomeHeaderContainer'
import MyLayout from '@web/pages/v5/home/MyLayout'
import ProfileSectionContainer from '@web/pages/v5/home/ProfileSection'
import ShortcutSectionContainer from '@web/pages/v5/home/ShortcutSection'
import { UpcomingBookingQuery } from '@web/pages/v5/home/UpcomingSection/hooks/useUpcomingBooking.query'
import { UpcomingOrderQuery } from '@web/pages/v5/home/UpcomingSection/hooks/useUpcomingOrder.query'
import { UpcomingTrainTicketQuery } from '@web/pages/v5/home/UpcomingSection/hooks/useUpcomingTrainTicket.query'
import FirstZone from '@web/pages/v5/home/Zone/FirstZone'
import { AppServerSideProps } from '@web/types/app'
import Loading from '@web/ui/components/Loading'

const DynamicSecondZone = lazy(() => import('@web/pages/v5/home/Zone/SecondZone'))

const MeQuery = gql(`
  query ssrMe {
    ...MyMe_Query
  }
  fragment MyMe_Query on Query {
    me  {
      ...on MeSucceed {
        user {
          ...MyMe_User
        }
      }
    }
  }

  fragment MyMe_User on User {
    ...HomeHeader_User
    ...ProfileSectionContainer_User
    ...CalendarSection_User
    ...LocationAgreement_User
    ...StatisticsMyReviewSection_User
    ...RecentOrderBookingSection_User
    ...FooterSection_User
    ...ShortcutSectionContainer_User
  }
`)

const SSRQuery = gql(`
  query ssrQuery {
    ...MySections_Query
    ...DailyBriefingSection_Query
    ...DdayRecommendPlacesSection_Query
  }
`)

const FirstZoneSectionsQuery = gql(`
  query firstZoneSections {
    ...MissionSection_Query
    ...TimelineSection_Query
    ...FavoritesSection_Query
  }
`)

const SecondZoneRenderedVar = makeVar(false)
export const LAST_ELEMENT_ID = 'firstZoneEnd'

const My = () => {
  useAppBgColor(AppBgColors.GRAY)
  const { data: meData, loading: meLoading, error: meError } = useQuery(MeQuery)
  const { loading: loadingFirstZone } = useQuery(FirstZoneSectionsQuery)

  if (meError) return <ErrorPage />

  if (meLoading || !meData) return <Loading isPageType colored />

  const { MeSucceed } = extractByTypename(meData.me)
  const user = MeSucceed?.user

  const title = user?.profile.nickname ? `${user.profile.nickname}님의 MY플레이스` : '네이버 MY플레이스'

  return (
    <MyLayout
      title={title}
      refreshClickCode={CLICK_CODE['my.pagereload']}
      topBtnClickCode={CLICK_CODE['my.topbttn']}
      metaTagProps={{ title }}
    >
      {/* 기본 헤더 */}
      <HomeHeaderContainer user={user} />
      {/* 프로필 */}
      <ProfileSectionContainer user={user} />
      {/* 숏컷 섹션 */}
      <ShortcutSectionContainer user={user} />
      {loadingFirstZone ? <ViewCardImageLoader /> : <FirstZone user={user} />}
      <div id={LAST_ELEMENT_ID} />
      {!loadingFirstZone && (
        <LazyIntersectionLoader fallback={<ViewCardImageLoader />} renderedVar={SecondZoneRenderedVar}>
          <DynamicSecondZone user={user} />
        </LazyIntersectionLoader>
      )}
      {/* 리뷰 쓰기 버튼 */}
      <CheckInButtonContainer />
      {!loadingFirstZone && <FooterSection user={user} />}
    </MyLayout>
  )
}

export const getServerSideProps: AppServerSideProps = async context => {
  const apolloClient = initializeFederationApollo({ context })

  try {
    const [meResponse, ssrResponse] = await Promise.allSettled([
      apolloClient.query({ query: MeQuery }),
      apolloClient.query({ query: SSRQuery }),
      apolloClient.query({ query: UpcomingBookingQuery, variables: { limit: UPCOMING_PAGE_SIZE } }),
      apolloClient.query({ query: UpcomingOrderQuery, variables: { limit: UPCOMING_PAGE_SIZE } }),
      apolloClient.query({ query: UpcomingTrainTicketQuery, variables: { limit: UPCOMING_PAGE_SIZE } }),
    ])

    if (meResponse.status === 'rejected') {
      neloLogger('error', `MY통합 홈 MeQuery 쿼리 호출 실패 ${extractErrorMessage(meResponse.reason)}`)
    }

    if (ssrResponse.status === 'rejected') {
      neloLogger('error', `MY통합 홈 SSRQuery 쿼리 호출 실패 ${extractErrorMessage(ssrResponse.reason)}`)
    }

    return {
      props: {
        initialState: apolloClient.cache.extract(),
      },
    }
  } catch (e) {
    neloLogger('error', `MY통합 홈 SSR 쿼리 호출 실패 ${extractErrorMessage(e)}`)
    return {
      props: {
        initialState: apolloClient.cache.extract(),
      },
    }
  }
}

export default My
